import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { detectEcommercePlatform, isCsvImportConnection, INVENTORY_DISPLAY_PLATFORMS } from '@/shared/connectionPlatform';

// Group inventory data by SKU, combining locations and summing quantities
const groupInventoryBySku = (data: any[]): any[] => {
  if (!data || data.length === 0) {
    return data;
  }

  const grouped = data.reduce((acc, item) => {
    // Try multiple possible SKU field names (case-insensitive search)
    let sku = null;
    const skuFields = ['sku', 'SKU', 'SKU (Unique Identifier)', 'product_sku', 'variant_sku', 'seller_sku', 'Sku', 'Product_SKU', 'PRODUCT_SKU'];

    for (const field of skuFields) {
      if (item[field]) {
        sku = item[field];
        break;
      }
    }

    // Also try case-insensitive field search
    if (!sku) {
      const fieldNames = Object.keys(item);
      const skuField = fieldNames.find(field =>
        field.toLowerCase().includes('sku') ||
        field.toLowerCase() === 'sku'
      );
      if (skuField) {
        sku = item[skuField];
      }
    }

    if (!sku) {
      // Return item as-is if no SKU found
      return [...acc, item];
    }

    const key = sku.toString().toLowerCase().trim();

    // Find existing item with same SKU
    const existingIndex = acc.findIndex((accItem: any) => {
      const accSku = accItem.sku || accItem.SKU || accItem['SKU (Unique Identifier)'] || accItem.product_sku || accItem.variant_sku || accItem.seller_sku;
      return accSku && accSku.toString().toLowerCase().trim() === key;
    });

    if (existingIndex !== -1) {
      // Merge with existing item
      const existing = acc[existingIndex];

      // Get source/location for both items
      const existingLocation = existing.location || existing.Location || existing.source || existing.Source || existing.marketplace || 'Unknown';
      const newLocation = item.location || item.Location || item.source || item.Source || item.marketplace || 'Unknown';

      // Merge locations
      const locations = [existingLocation, newLocation]
        .filter(loc => loc && loc.trim() && loc !== 'Unknown')
        .filter((value, index, array) => array.indexOf(value) === index); // Remove duplicates
      const combinedLocation = locations.join(', ') || 'Unknown';

      // Sum quantities - try multiple quantity field names
      const existingQty = parseFloat(existing.quantity || existing.Quantity || existing.stock_level || existing.available_quantity || existing.held || existing.Held || '0') || 0;
      const newQty = parseFloat(item.quantity || item.Quantity || item.stock_level || item.available_quantity || item.held || item.Held || '0') || 0;
      const summedQty = existingQty + newQty;

      // Create merged item keeping original structure but updating key fields
      const mergedItem = { ...existing };

      // Determine source names for existing and new items
      const existingSourceName = (existingLocation || 'Unknown').toString().split(',')[0].trim() || 'Unknown';
      const newSourceName = (newLocation || 'Unknown').toString().split(',')[0].trim() || 'Unknown';

      // Initialize or update source breakdown before changing quantities
      if (!('sourceBreakdown' in mergedItem) || !mergedItem.sourceBreakdown) {
        (mergedItem as any).sourceBreakdown = {} as Record<string, number>;
      }
      if (Object.keys((mergedItem as any).sourceBreakdown).length === 0 && (existingQty ?? 0) >= 0) {
        (mergedItem as any).sourceBreakdown[existingSourceName] = ((mergedItem as any).sourceBreakdown[existingSourceName] || 0) + existingQty;
      }
      (mergedItem as any).sourceBreakdown[newSourceName] = ((mergedItem as any).sourceBreakdown[newSourceName] || 0) + newQty;

      // Update location fields
      if (mergedItem.location !== undefined) mergedItem.location = combinedLocation;
      if (mergedItem.Location !== undefined) mergedItem.Location = combinedLocation;
      if (mergedItem.source !== undefined) mergedItem.source = combinedLocation;
      if (mergedItem.Source !== undefined) mergedItem.Source = combinedLocation;
      if (mergedItem.marketplace !== undefined) mergedItem.marketplace = combinedLocation;

      // Update quantity fields (store total/summed)
      if (mergedItem.quantity !== undefined) mergedItem.quantity = summedQty.toString();
      if (mergedItem.Quantity !== undefined) mergedItem.Quantity = summedQty.toString();
      if (mergedItem.stock_level !== undefined) mergedItem.stock_level = summedQty.toString();
      if (mergedItem.available_quantity !== undefined) mergedItem.available_quantity = summedQty.toString();
      if (mergedItem.held !== undefined) mergedItem.held = summedQty.toString();
      if (mergedItem.Held !== undefined) mergedItem.Held = summedQty.toString();

      // Handle ASIN - preserve existing or add new
      const existingAsin = existing.asin || existing.ASIN || existing['ASIN'];
      const newAsin = item.asin || item.ASIN || item['ASIN'];
      if (newAsin && !existingAsin) {
        if (mergedItem.asin !== undefined) mergedItem.asin = newAsin;
        if (mergedItem.ASIN !== undefined) mergedItem.ASIN = newAsin;
        if (mergedItem['ASIN'] !== undefined) mergedItem['ASIN'] = newAsin;
      }

      // Handle GTIN - preserve existing or add new
      const existingGtin = existing.gtin || existing.GTIN || existing['GTIN'];
      const newGtin = item.gtin || item.GTIN || item['GTIN'];
      if (newGtin && !existingGtin) {
        if (mergedItem.gtin !== undefined) mergedItem.gtin = newGtin;
        if (mergedItem.GTIN !== undefined) mergedItem.GTIN = newGtin;
        if (mergedItem['GTIN'] !== undefined) mergedItem['GTIN'] = newGtin;
      }

      // Handle product descriptions by source
      const existingDesc = existing.product_description || existing['Product Description'] || existing['Product Description (Amazon)'] || existing['Product Description (Shopify)'] || '';
      const newDesc = item.product_description || item['Product Description'] || item['Product Description (Amazon)'] || item['Product Description (Shopify)'] || '';

      if (existingDesc && newDesc && existingDesc !== newDesc) {
        // Create source-specific descriptions
        const existingSource = existingLocation.split(',')[0].trim();
        const newSource = newLocation.split(',')[0].trim();

        if (mergedItem['Product Description']) {
          delete mergedItem['Product Description'];
        }
        if (mergedItem.product_description) {
          delete mergedItem.product_description;
        }

        mergedItem[`Product Description (${existingSource})`] = existingDesc;
        mergedItem[`Product Description (${newSource})`] = newDesc;
      } else if (newDesc && !existingDesc) {
        // Update with new description if existing was empty
        if (mergedItem.product_description !== undefined) mergedItem.product_description = newDesc;
        if (mergedItem['Product Description'] !== undefined) mergedItem['Product Description'] = newDesc;

        // Also handle source-specific descriptions
        const newSource = newLocation.split(',')[0].trim();
        if (item[`Product Description (${newSource})`]) {
          mergedItem[`Product Description (${newSource})`] = item[`Product Description (${newSource})`];
        }
      } else if (existingDesc && newDesc && existingDesc === newDesc) {
        // Same description from different sources - keep the existing one
        // But also check for source-specific description fields
        const newSource = newLocation.split(',')[0].trim();
        if (item[`Product Description (${newSource})`] && !mergedItem[`Product Description (${newSource})`]) {
          mergedItem[`Product Description (${newSource})`] = item[`Product Description (${newSource})`];
        }
      }

      acc[existingIndex] = mergedItem;
    } else {
      // First occurrence of this SKU
      const initialLocation = item.location || item.Location || item.source || item.Source || item.marketplace || 'Unknown';
      const initialSource = (initialLocation || 'Unknown').toString().split(',')[0].trim() || 'Unknown';
      const initialQty = parseFloat(item.quantity || item.Quantity || item.stock_level || item.available_quantity || item.held || item.Held || '0') || 0;
      const initialItem: any = { ...item };
      if (!initialItem.sourceBreakdown) {
        initialItem.sourceBreakdown = { [initialSource]: initialQty } as Record<string, number>;
      }
      acc.push(initialItem);
    }

    return acc;
  }, [] as any[]);

  return grouped;
};

// Group timeseries inventory data by SKU and Date, combining sources
const groupInventoryBySkuAndDate = (data: any[]): any[] => {
  if (!data || data.length === 0) {
    return data;
  }

  const grouped = data.reduce((acc, item) => {
    // Extract SKU
    let sku = null;
    const skuFields = ['sku', 'SKU', 'SKU (Unique Identifier)', 'product_sku', 'variant_sku', 'seller_sku'];

    for (const field of skuFields) {
      if (item[field]) {
        sku = item[field];
        break;
      }
    }

    if (!sku) {
      const fieldNames = Object.keys(item);
      const skuField = fieldNames.find(field => field.toLowerCase().includes('sku'));
      if (skuField) {
        sku = item[skuField];
      }
    }

    // Extract date
    const date = item.date;

    if (!sku || !date) {
      return acc;
    }

    // Create composite key: sku + date
    const key = `${sku}|||${date}`;

    // Determine source
    const source = item.source || item.Source || item.location || item.Location || item.marketplace || 'Unknown';

    // Find existing item with same SKU + date
    const existingIndex = acc.findIndex((accItem: any) => {
      const accSku = accItem.sku || accItem.SKU || accItem['SKU (Unique Identifier)'];
      const accDate = accItem.date;
      return accSku && accDate &&
        accSku.toString().toLowerCase().trim() === sku.toString().toLowerCase().trim() &&
        accDate === date;
    });

    if (existingIndex !== -1) {
      // Merge with existing item
      const existing = acc[existingIndex];

      // Sum quantities
      const existingQty = parseFloat(existing.quantity || existing.Quantity || existing.held || existing.Held || '0') || 0;
      const newQty = parseFloat(item.quantity || item.Quantity || item.held || item.Held || '0') || 0;
      const summedQty = existingQty + newQty;

      // Update quantity
      if (existing.quantity !== undefined) existing.quantity = summedQty;
      if (existing.Quantity !== undefined) existing.Quantity = summedQty;
      if (existing.held !== undefined) existing.held = summedQty;
      if (existing.Held !== undefined) existing.Held = summedQty;

      // Initialize or update source breakdown
      if (!existing.sourceBreakdown) {
        existing.sourceBreakdown = {};
      }
      existing.sourceBreakdown[source] = (existing.sourceBreakdown[source] || 0) + newQty;

      // Combine source strings
      const existingSource = existing.source || existing.Source || existing.location || existing.Location || '';
      if (existingSource && !existingSource.includes(source)) {
        if (existing.source !== undefined) existing.source = `${existingSource}, ${source}`;
        if (existing.Source !== undefined) existing.Source = `${existingSource}, ${source}`;
        if (existing.location !== undefined) existing.location = `${existingSource}, ${source}`;
        if (existing.Location !== undefined) existing.Location = `${existingSource}, ${source}`;
      } else if (!existingSource) {
        if (existing.source !== undefined) existing.source = source;
        if (existing.Source !== undefined) existing.Source = source;
        if (existing.location !== undefined) existing.location = source;
        if (existing.Location !== undefined) existing.Location = source;
      }

      acc[existingIndex] = existing;
    } else {
      // First occurrence of this SKU + date
      const initialQty = parseFloat(item.quantity || item.Quantity || item.held || item.Held || '0') || 0;
      const initialItem: any = { ...item };

      if (!initialItem.sourceBreakdown) {
        initialItem.sourceBreakdown = { [source]: initialQty };
      }

      acc.push(initialItem);
    }

    return acc;
  }, [] as any[]);

  return grouped.map((item: any) => {
    const keys = item.sourceBreakdown ? Object.keys(item.sourceBreakdown) : [];
    const unique = Array.from(new Set(keys.map((k) => String(k).trim()))).filter(Boolean);
    const joined = unique.join(', ');
    if (item.source !== undefined) item.source = joined;
    if (item.Source !== undefined) item.Source = joined;
    if (item.location !== undefined) item.location = joined;
    if (item.Location !== undefined) item.Location = joined;
    return item;
  });
};

interface ProgressiveInventoryData {
  data: any[];
  fullData: any[]; // For graph view - all grouped data without pagination
  loading: boolean;
  loadingBySource: Record<string, boolean>;
  completedSources: string[];
  error: string | null;
  totalCount: number;
  generating: boolean;
  generationReason: 'mapping_change' | null;
}

interface UseProgressiveInventoryOptions {
  organisation: any;
  reportType: string;
  sources: string[];
  dateRange: 'trailing-12' | 'custom';
  customStartDate?: Date;
  customEndDate?: Date;
  pageSize?: number;
  viewMode?: 'snapshot' | 'timeseries';
  selectedSkus?: string[];
  onDataUpdate?: (data: any[]) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  sortColumn?: string | null;
  sortDirection?: 'asc' | 'desc';
  graphViewActive?: boolean;
  columnFilters?: Array<{ column: string; value: string; operation: string }>;
}

export const useProgressiveInventory = ({ organisation, reportType, sources, dateRange, customStartDate, customEndDate, pageSize, viewMode = 'snapshot', selectedSkus, onDataUpdate, page = 1, onPageChange, sortColumn, sortDirection, graphViewActive = false, columnFilters = [] }: UseProgressiveInventoryOptions) => {
  const [inventoryState, setInventoryState] = useState<ProgressiveInventoryData>({
    data: [],
    fullData: [],
    loading: false,
    loadingBySource: {},
    completedSources: [],
    error: null,
    totalCount: 0,
    generating: false,
    generationReason: null
  });

  // Cache for storing fetched data per connection
  const cacheRef = useRef<Record<string, { data: any[], filterKey: string }>>({});
  // Dedup keys to avoid duplicate effect-triggered fetches (e.g., React StrictMode)
  const lastFetchKeyRef = useRef<string | null>(null);
  const inFlightKeyRef = useRef<string | null>(null);
  const pendingKeyRef = useRef<string | null>(null);
  // Cache for fullData to avoid refetching when switching between table and graph views
  const fullDataCacheRef = useRef<{ key: string, data: any[] } | null>(null);

  // Generate a normalized stable key for current filters (excluding pageSize and page)
  const getFilterKey = useCallback((filters: any) => {
    const dateKey = `${filters.startDate || ''}:${filters.endDate || ''}`;
    const skusKey = selectedSkus && selectedSkus.length > 0 ? selectedSkus.sort().join(',') : 'all';
    // Normalize columnFilters for stable cache key
    const columnFiltersKey = filters.columnFilters && filters.columnFilters.length > 0
      ? JSON.stringify(filters.columnFilters.map((f: any) => ({ c: f.column, o: f.operation, v: f.value })).sort((a: any, b: any) => a.c.localeCompare(b.c)))
      : 'none';
    return JSON.stringify({
      reportType: filters.reportType,
      dateKey,
      viewMode: filters.viewMode || 'snapshot',
      skus: skusKey,
      sortColumn: sortColumn || null,
      sortDirection: sortDirection || 'desc',
      columnFilters: columnFiltersKey,
    });
  }, [selectedSkus, sortColumn, sortDirection]);

  const fetchInventoryData = useCallback(async (inventoryType: string, filters: any) => {

    // Don't reset fullData if we're adding/removing SKUs in graph view - keep existing data visible
    const shouldPreserveFullData = filters.viewMode === 'timeseries' && graphViewActive && fullDataCacheRef.current?.data?.length > 0;

    setInventoryState({
      data: [],
      fullData: shouldPreserveFullData ? fullDataCacheRef.current!.data : [],
      loading: true,
      loadingBySource: {},
      completedSources: [],
      error: null,
      totalCount: 0,
      generating: false,
      generationReason: null
    });

    // Let the backend resolve clone/source-org inventory availability.
    // Frontend org metadata may be empty for test clones even when the source org has data.
    const hasSnapshotData = organisation?.snapshot_table_created === true;
    const connections = Object.values(organisation?.connections || {}) as any[];
    const availableConnections = connections.filter((conn: any) => {
      const platform = detectEcommercePlatform(conn);
      if (platform !== null) return true;

      const ecommerceSubtype = conn.ecommerce_subtype?.toLowerCase() || '';
      if (isCsvImportConnection(conn) && (INVENTORY_DISPLAY_PLATFORMS as readonly string[]).includes(ecommerceSubtype)) {
        return true;
      }

      return false;
    });

    // Only short-circuit when we have enough local evidence for a non-clone org.
    // Clone orgs may intentionally have no direct connections on the frontend payload.
    if (!organisation?.is_test_clone && availableConnections.length === 0 && !hasSnapshotData) {
      setInventoryState(prev => ({
        ...prev,
        data: [],
        fullData: [],
        loading: false,
        error: 'No supported connections found for inventory data',
        totalCount: 0
      }));
      return;
    }

    try {
      // SIMPLIFIED APPROACH: Single backend call that handles all sources and grouping
      // Backend will fetch from all sources, group by SKU (or SKU+Date), and paginate the grouped results

      // First, fetch total count
      const { data: countData, error: countError } = await invokeFunction(supabase, 'inventory-data-v6', {
        body: {
          inventoryType,
          organisationId: organisation.id,
          filters: {
            ...filters,
            viewMode: filters.viewMode || 'snapshot',
            dataSource: 'All', // Fetch from all sources
            skus: selectedSkus || filters.skus,
            columnFilters: filters.columnFilters
          },
          sortColumn,
          sortDirection,
          countOnly: true
        }
      });

      if (countError) {
        throw countError;
      }

      const totalCount = countData?.totalCount || 0;

      setInventoryState(prev => ({
        ...prev,
        totalCount
      }));

      // Now fetch paginated data - backend will handle grouping before pagination
      const { data: inventoryData, error: dataError } = await invokeFunction(supabase, 'inventory-data-v6', {
        body: {
          inventoryType,
          organisationId: organisation.id,
          filters: {
            ...filters,
            viewMode: filters.viewMode || 'snapshot',
            dataSource: 'All', // Backend handles all sources
            skus: selectedSkus || filters.skus,
            columnFilters: filters.columnFilters
          },
          sortColumn,
          sortDirection,
          page: page,
          pageSize: filters.pageSize || pageSize || 50,
          countOnly: false
        }
      });

      if (dataError) {
        throw dataError;
      }

      const fetchedData = inventoryData?.data || [];

      // For time series view, also fetch ALL data for the graph (only when graph is actually being viewed)
      let fullDataset = fetchedData;
      if (filters.viewMode === 'timeseries' && selectedSkus && selectedSkus.length > 0 && graphViewActive) {
        // Check if we have cached fullData for current filters
        const fullDataCacheKey = `${inventoryType}|${organisation.id}|${filters.startDate}:${filters.endDate}|skus:${selectedSkus.sort().join(',')}`;

        if (fullDataCacheRef.current?.key === fullDataCacheKey) {
          fullDataset = fullDataCacheRef.current.data;
        } else {
          const { data: allData, error: allDataError } = await invokeFunction(supabase, 'inventory-data-v6', {
            body: {
              inventoryType,
              organisationId: organisation.id,
              filters: {
                ...filters,
                viewMode: 'timeseries',
                dataSource: 'All',
                skus: selectedSkus || filters.skus,
                columnFilters: filters.columnFilters
              },
              sortColumn: 'date',
              sortDirection: 'asc',
              page: 1,
              pageSize: 100000,
              countOnly: false
            }
          });

          if (!allDataError && allData?.data) {
            fullDataset = allData.data;
            // Cache the fullData
            fullDataCacheRef.current = {
              key: fullDataCacheKey,
              data: fullDataset
            };
          }
        }
      } else if (filters.viewMode === 'timeseries' && graphViewActive && (!selectedSkus || selectedSkus.length === 0)) {
        // When no SKUs selected in graph view, fetch ALL SKUs' data for aggregation
        const fullDataCacheKey = `${inventoryType}|${organisation.id}|${filters.startDate}:${filters.endDate}|nosku`;

        if (fullDataCacheRef.current?.key === fullDataCacheKey && fullDataCacheRef.current.data.length > 0) {
          // Use cached data if available
          fullDataset = fullDataCacheRef.current.data;
        } else {
          // Fetch all data without SKU filter
          const { data: allData, error: allDataError } = await invokeFunction(supabase, 'inventory-data-v6', {
            body: {
              inventoryType,
              organisationId: organisation.id,
              filters: {
                ...filters,
                viewMode: 'timeseries',
                dataSource: 'All',
                skus: [] // Empty array means fetch all SKUs
              },
              sortColumn: 'date',
              sortDirection: 'asc',
              page: 1,
              pageSize: 100000, // Large number to get all data
              countOnly: false
            }
          });

          if (!allDataError && allData?.data) {
            fullDataset = allData.data;
            // Cache the fullData
            fullDataCacheRef.current = {
              key: fullDataCacheKey,
              data: fullDataset
            };
          }
        }
      }

      const mappingGenerating = inventoryData?.generating === true && inventoryData?.generationReason === 'mapping_change';
      if (mappingGenerating) {
        fullDataCacheRef.current = null;
      }

      // Backend already grouped and paginated the data
      setInventoryState(prev => ({
        ...prev,
        data: fetchedData,
        fullData: fullDataset,
        loading: false,
        completedSources: ['All'],
        totalCount,
        generating: mappingGenerating,
        generationReason: mappingGenerating ? 'mapping_change' : null
      }));

      if (onDataUpdate) {
        onDataUpdate(fetchedData);
      }

    } catch (error) {
      console.error('Error fetching inventory data:', error);
      setInventoryState(prev => ({
        ...prev,
        data: [],
        fullData: [],
        loading: false,
        error: error.message || 'Failed to fetch inventory data',
        totalCount: 0
      }));
    }
  }, [organisation, selectedSkus, pageSize, onDataUpdate, sortColumn, sortDirection, page, graphViewActive]);

  // Auto-fetch data when dependencies change
  useEffect(() => {
    if (!organisation?.id) return;
    if (!sources || sources.length === 0) return;

    const filters = {
      dataSources: sources,
      startDate: customStartDate ? format(customStartDate, 'yyyy-MM-dd') : format(startOfMonth(subMonths(new Date(), 11)), 'yyyy-MM-dd'),
      endDate: customEndDate ? format(customEndDate, 'yyyy-MM-dd') : format(endOfMonth(new Date()), 'yyyy-MM-dd'),
      trailing12Months: dateRange === 'trailing-12',
      pageSize: pageSize || 50,
      viewMode: viewMode || 'snapshot',
      page: page,
      columnFilters: columnFilters || []
    };

    // Build a stable key to deduplicate duplicate effect runs and coalesce rapid changes
    const baseKey = getFilterKey(filters);
    const sourcesKey = Array.isArray(sources) ? [...sources].sort().join(',') : '';
    const currentKey = `${baseKey}|${sourcesKey}|page:${page}|pageSize:${pageSize}|sort:${sortColumn || 'none'}:${sortDirection}|graph:${graphViewActive ? '1' : '0'}`;

    // Check if we already have a pending/in-flight/completed request for this key
    if (inFlightKeyRef.current === currentKey ||
      lastFetchKeyRef.current === currentKey ||
      pendingKeyRef.current === currentKey) {
      return;
    }

    // Mark this key as pending
    pendingKeyRef.current = currentKey;

    const timeoutId = window.setTimeout(() => {
      // Double-check in case another effect run completed while we were waiting
      if (inFlightKeyRef.current === currentKey || lastFetchKeyRef.current === currentKey) {
        pendingKeyRef.current = null;
        return;
      }
      inFlightKeyRef.current = currentKey;
      pendingKeyRef.current = null;

      fetchInventoryData(reportType, filters)
        .finally(() => {
          lastFetchKeyRef.current = currentKey;
          inFlightKeyRef.current = null;
        });
    }, 120); // debounce to allow selectedSkus/state to settle when switching tabs

    return () => {
      window.clearTimeout(timeoutId);
      // Clear pending flag if timeout is cancelled
      if (pendingKeyRef.current === currentKey) {
        pendingKeyRef.current = null;
      }
    };
  }, [organisation?.id, reportType, sources, dateRange, customStartDate, customEndDate, pageSize, viewMode, selectedSkus, page, sortColumn, sortDirection, graphViewActive, columnFilters, getFilterKey, fetchInventoryData]);

  return {
    data: inventoryState.data,
    fullData: inventoryState.fullData, // For graph view
    isLoading: inventoryState.loading,
    error: inventoryState.error,
    totalCount: inventoryState.totalCount,
    platforms: inventoryState.completedSources,
    generating: inventoryState.generating,
    generationReason: inventoryState.generationReason,
    refetch: () => {
      if (!sources || sources.length === 0) return;
      fullDataCacheRef.current = null;
      lastFetchKeyRef.current = null;

      const filters = {
        dataSources: sources,
        startDate: customStartDate ? format(customStartDate, 'yyyy-MM-dd') : format(startOfMonth(subMonths(new Date(), 11)), 'yyyy-MM-dd'),
        endDate: customEndDate ? format(customEndDate, 'yyyy-MM-dd') : format(endOfMonth(new Date()), 'yyyy-MM-dd'),
        trailing12Months: dateRange === 'trailing-12',
        pageSize: pageSize || 50,
        viewMode: viewMode || 'snapshot',
        page: page
      };

      fetchInventoryData(reportType, filters);
    },
    fetchInventoryData
  };
};